import { GPSSource, PosterProject } from "PosterEditor/model/poster-project";
import { Point, Size, PointCoordinate, Rect } from "../model/poster-types";
import {
  MapLabel,
  StaticLabel,
  Backdrop,
} from "PosterEditor/model/poster-project";
import { drawBackdrops } from "./canvas-drawing/canvas-drawing-backdrops";
import { drawRouteLines } from "./canvas-drawing/canvas-drawing-route-lines";
import { drawPoints } from "./canvas-drawing/canvas-drawing-points";
import { drawMapLabels } from "./canvas-drawing/canvas-drawing-map-labels";
import { drawStaticLabels } from "./canvas-drawing/canvas-drawing-static-labels";
import { drawData } from "./canvas-drawing/canvas-drawing-data";
import { drawDotGrid } from "./canvas-drawing/canvas-drawing-dot-grid";
import { Element } from "../model/poster-types";
import { SnapLine } from "./rect-snapping";
import { EditingPermissions } from "PosterEditor/model/editing-permissions";
export interface MapDrawingConfig {
  map: React.MutableRefObject<any>;
  canvasElement: React.MutableRefObject<any>;
  originalMapSize: Size;
  scale: number;
  project: PosterProject;
  gpsSource: GPSSource | undefined;
  pointerLocation?: Point;
  dotGridSpacing?: number;
  pointerInteruptRadius: number;
  elementToHighlight: MapElement | undefined;
  selectedElement: Element | undefined;
  isRendering: boolean;
  projectEditingPermissions: EditingPermissions;
  isTemplateEditing: boolean;
}

export interface DragHandle {
  position: "leading" | "trailing";
}

export interface MapElement {
  type: "point" | "mapLabel" | "staticLabel" | "backdrop";
  //Point has no element, it's just a point.
  pointCoordinate?: PointCoordinate;
  rect: Rect;
  element?: MapLabel | StaticLabel | Backdrop;
  dragHandleUnderCursor?: DragHandle;
}

export const drawMapElements = (
  config: MapDrawingConfig
): {
  elementUnderPointerLocation: MapElement | undefined;
  snapLines: {
    vertical: SnapLine | undefined;
    horizontal: SnapLine | undefined;
  };
} => {
  const ctx = config.canvasElement.current.getContext("2d");
  if (!config.map.current || !ctx)
    return {
      elementUnderPointerLocation: undefined,
      snapLines: { vertical: undefined, horizontal: undefined },
    };

  ctx.clearRect(
    0,
    0,
    config.canvasElement.current.width,
    config.canvasElement.current.height
  );

  // DRAW ROUTE LINES
  const normalPointUnderCursor =
    drawRouteLines(config).elementUnderPointerLocation;

  const backdropUnderCursor = drawBackdrops(config).elementUnderPointerLocation;

  // DRAW LABELS
  const mapLabelUnderCursor = drawMapLabels(config).elementUnderPointerLocation;

  // DRAW STYLED POINTS
  const styledPointUnderCursor = drawPoints(config).elementUnderPointerLocation;

  //DRAW STATIC LABELS
  const staticLabelResult = drawStaticLabels(config);
  const staticLabelUnderCursor = staticLabelResult.elementUnderPointerLocation;

  if (
    config.selectedElement?.type === "backdrop" ||
    config.selectedElement?.type === "staticLabel"
  ) {
    drawDotGrid(config);
  }
  return {
    elementUnderPointerLocation:
      staticLabelUnderCursor ||
      mapLabelUnderCursor ||
      styledPointUnderCursor ||
      backdropUnderCursor ||
      normalPointUnderCursor,
    snapLines: {
      vertical: undefined, // staticLabelResult.snapLines.vertical,
      horizontal: undefined, //staticLabelResult.snapLines.horiztonal,
    },
  };
};

// Shared canvas drawing functions.

export const drawSelectedRect = (
  textRect: Rect,
  ctx: CanvasRenderingContext2D
) => {
  drawDottedRect(textRect, ctx);
};

export const drawDottedRect = (
  textRect: Rect,
  ctx: CanvasRenderingContext2D,
  dotSize = 2, // Size of each dot
  gapSize = 1 // Space between dots
) => {
  const { x, y, width, height } = textRect;
  let isBlack = true; // Alternate between black & white

  ctx.lineWidth = dotSize;

  const drawDot = (px: number, py: number) => {
    ctx.fillStyle = isBlack ? "#636363" : "#D0D0D0";
    ctx.beginPath();
    ctx.arc(px, py, dotSize / 2, 0, Math.PI * 2);
    ctx.fill();
    isBlack = !isBlack; // Toggle color
  };

  // Top edge
  for (let px = x; px < x + width; px += dotSize + gapSize) drawDot(px, y);

  // Right edge
  for (let py = y; py < y + height; py += dotSize + gapSize)
    drawDot(x + width, py);

  // Bottom edge
  for (let px = x + width; px > x; px -= dotSize + gapSize)
    drawDot(px, y + height);

  // Left edge
  for (let py = y + height; py > y; py -= dotSize + gapSize) drawDot(x, py);
};
